import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import { authHeadingClass, authLinkClass } from '../../components/auth/authTheme'
import { moduleTaskLabelKey } from '../../hooks/notifications/types'
import { api } from '../../lib/api'

type UnsubResult = {
  ok: boolean
  module?: string | null
  notification_type?: string | null
}

export default function NotificationUnsubscribe() {
  const { t } = useTranslation(['profile', 'dashboard', 'common'])
  const [params] = useSearchParams()
  const token = params.get('token')
  const statusParam = params.get('status')
  const moduleParam = params.get('module') || undefined
  const typeParam = params.get('type') || undefined

  const [phase, setPhase] = useState<'idle' | 'loading' | 'done'>(() =>
    token ? 'loading' : 'idle',
  )
  const [result, setResult] = useState<UnsubResult | null>(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get<UnsubResult>(
          '/notifications/email-unsubscribe',
          {
            params: { token },
            headers: { Accept: 'application/json' },
          },
        )
        if (!cancelled) {
          setResult(data)
          setPhase('done')
        }
      } catch {
        if (!cancelled) {
          setResult({ ok: false })
          setPhase('done')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const ok =
    result?.ok === true ||
    (!token && statusParam === 'ok')
  const module = result?.module || moduleParam
  const type = result?.notification_type || typeParam
  const showResult = phase === 'done' || (!token && statusParam != null)

  const typeLabel =
    type === 'generation_failed'
      ? t('profile:notifications.failedColumn')
      : type === 'generation_ready'
        ? t('profile:notifications.readyColumn')
        : null

  const moduleLabel = module
    ? t(`dashboard:${moduleTaskLabelKey(module)}`)
    : null

  return (
    <AuthShell>
      <div className="text-center">
        <AuthBrand />
        {phase === 'loading' && (
          <>
            <div className="mx-auto mt-6 flex items-center justify-center h-12 w-12 rounded-full bg-[#1a1a1a] border border-gray-700">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5A00]" />
            </div>
            <h2 className={`mt-6 ${authHeadingClass}`}>
              {t('profile:notifications.unsubscribe.loadingHeading')}
            </h2>
          </>
        )}
        {showResult && (
          <>
            <h2 className={`mt-6 ${authHeadingClass}`}>
              {ok
                ? t('profile:notifications.unsubscribe.successHeading')
                : t('profile:notifications.unsubscribe.errorHeading')}
            </h2>
            <p className="mt-3 text-sm text-gray-400">
              {ok
                ? t('profile:notifications.unsubscribe.successBody', {
                    module:
                      moduleLabel ||
                      t('profile:notifications.unsubscribe.genericCategory'),
                    type:
                      typeLabel ||
                      t('profile:notifications.unsubscribe.genericType'),
                  })
                : t('profile:notifications.unsubscribe.errorBody')}
            </p>
            <div className="mt-8 space-y-3">
              <Link
                to="/profile#notification-preferences"
                className={`${authLinkClass} block`}
              >
                {t('profile:notifications.unsubscribe.manageLink')}
              </Link>
              <Link to="/dashboard" className={`${authLinkClass} block`}>
                {t('profile:notifications.unsubscribe.dashboardLink')}
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  )
}
